import type { Route } from '@hero/schema';
import { getRouteDisplayText } from '@hero/core';

/** Org id string equality (handles ObjectId / Id mixed shapes). */
export function isSameOrgId(a: unknown, b: unknown): boolean {
  if (!a || !b) return false;
  return String(a) === String(b);
}

/** Render a route's lane via {@link getRouteDisplayText} (LTR emoji arrows). */
export function getRouteLane(route: Pick<Route, 'destinations'>): string {
  if (!route.destinations?.length) return '';
  return getRouteDisplayText(
    { cities: route.destinations.map(d => d.displayName) },
    'en',
  );
}

/** Convenience: pull just the first destination as the route origin. */
export function getRouteOrigin(route: Pick<Route, 'destinations'>) {
  return route.destinations?.[0];
}

/** Convenience: everything after the origin is the destination chain. */
export function getRouteStops(route: Pick<Route, 'destinations'>) {
  return route.destinations?.slice(1) ?? [];
}

/**
 * Who is the org on this route — useful for inferring the form scenario.
 * Returns 'sell' when the org is the seller, 'buy' when it's the buyer, or
 * `undefined` if neither matches (shouldn't happen in practice).
 */
export function inferRouteSide(
  route: Pick<Route, 'seller' | 'buyer'>,
  orgId: string | undefined,
): 'sell' | 'buy' | undefined {
  if (!orgId) return undefined;
  if (route.seller && isSameOrgId(route.seller._id, orgId)) return 'sell';
  if (route.buyer && isSameOrgId(route.buyer._id, orgId)) return 'buy';
  return undefined;
}

/** Return the relation that ISN'T the current org (i.e. the counterparty). */
export function getRouteCounterparty(
  route: Pick<Route, 'seller' | 'buyer'>,
  orgId: string | undefined,
) {
  if (!orgId) return route.seller ?? route.buyer ?? undefined;
  if (route.seller && !isSameOrgId(route.seller._id, orgId)) return route.seller;
  if (route.buyer && !isSameOrgId(route.buyer._id, orgId)) return route.buyer;
  return undefined;
}
